import { Card, Skeleton } from '@ntconsult/aluma-react';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PercentageCheckSummary } from '../types/PercentageCheck';

type CardsProps = {
  summary: PercentageCheckSummary | undefined;
  isLoading: boolean;
};

export function PercentageCheckSummaryCards({ summary, isLoading }: CardsProps) {
  const { t } = useTranslation('alocacao');

  if (isLoading || !summary) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((idx) => (
          <Card key={idx}>
            <div className="flex flex-col gap-3 p-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-9 w-20" />
              <Skeleton className="h-3 w-36" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  const total = Math.max(1, summary.totalProfessionals);
  const percentOf = (value: number) => Math.round((value / total) * 100);

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        title={t('consultas.conferenciaPercentual.cards.abaixoTitulo')}
        description={t('consultas.conferenciaPercentual.cards.abaixoDescricao')}
        value={summary.totalBelow}
        percent={percentOf(summary.totalBelow)}
        icon={<TrendingDown size={18} aria-hidden />}
        toneClass="bg-warning-subtle text-warning"
      />
      <KpiCard
        title={t('consultas.conferenciaPercentual.cards.cemTitulo')}
        description={t('consultas.conferenciaPercentual.cards.cemDescricao')}
        value={summary.totalAt100}
        percent={percentOf(summary.totalAt100)}
        icon={<Minus size={18} aria-hidden />}
        toneClass="bg-success-subtle text-success"
      />
      <KpiCard
        title={t('consultas.conferenciaPercentual.cards.acimaTitulo')}
        description={t('consultas.conferenciaPercentual.cards.acimaDescricao')}
        value={summary.totalAbove}
        percent={percentOf(summary.totalAbove)}
        icon={<TrendingUp size={18} aria-hidden />}
        toneClass="bg-danger-subtle text-danger"
      />
      <KpiCard
        title={t('consultas.conferenciaPercentual.cards.zeroTitulo')}
        description={t('consultas.conferenciaPercentual.cards.zeroDescricao')}
        value={summary.totalZero}
        percent={percentOf(summary.totalZero)}
        icon={<Minus size={18} aria-hidden />}
        toneClass="bg-surface-hover text-foreground-muted"
      />
    </div>
  );
}

type KpiCardProps = {
  title: string;
  description: string;
  value: number;
  percent: number;
  icon: ReactNode;
  toneClass: string;
};

function KpiCard({ title, description, value, percent, icon, toneClass }: KpiCardProps) {
  return (
    <Card>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm text-foreground-muted">{title}</p>
            <p className="truncate text-xs text-foreground-muted">{description}</p>
          </div>
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${toneClass}`}
            aria-hidden
          >
            {icon}
          </span>
        </div>
        <div className="flex items-end justify-between gap-4">
          <span className="text-3xl font-semibold tabular-nums transition-all duration-300">
            {value}
          </span>
          <span className="text-sm text-foreground-muted tabular-nums">{percent}%</span>
        </div>
      </div>
    </Card>
  );
}
